import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
  Skeleton,
  Paper,
  Menu,
  MenuItem
} from '@mui/material';
import {
  AutoStories,
  QuestionAnswer,
  AccountTree,
  RecordVoiceOver,
  School,
  Assignment,
  Schedule,
  Logout,
  Grain,
  Chat
} from '@mui/icons-material';
import { auth } from '../utils/auth';

const features = [
  {
    icon: <AutoStories fontSize="large" />,
    title: 'AI Notes Generator',
    description: 'Upload PDFs or text to generate concise summaries.',
    action: 'Generate Notes',
    link: '/notes-generator'
  },
  {
    icon: <QuestionAnswer fontSize="large" />,
    title: 'Smart Flashcards',
    description: 'Create AI-powered flashcards with spaced repetition.',
    action: 'Create Flashcards',
    link: '/flashcards'
  },
  {
    icon: <AccountTree fontSize="large" />,
    title: 'Mind Maps',
    description: 'Visualize complex topics with AI-generated mind maps.',
    action: 'Create Mind Map',
    link: '/mindmap'
  },
  {
    icon: <Grain fontSize="large" />,
    title: 'Diagram Generator',
    description: 'Create diagrams, flowcharts, and visual aids from text descriptions.',
    action: 'Create Diagram',
    link: '/diagram-generator'
  },
  {
    icon: <RecordVoiceOver fontSize="large" />,
    title: 'Voice-to-Notes',
    description: 'Convert lecture recordings into structured notes.',
    action: 'Start Recording',
    link: '/voice-to-notes'
  },
  {
    icon: <Assignment fontSize="large" />,
    title: 'Sample Paper',
    description: 'Generate custom exam papers with marking scheme.',
    action: 'Create Paper',
    link: '/sample-paper',
    disabled: false
  },
  {
    icon: <Assignment fontSize="large" />,
    title: 'AI Practice Questions',
    description: 'Practice with AI-generated MCQs and explanations.',
    action: 'Start Practicing',
    link: '/practice-questions',
    disabled: false
  },
  {
    icon: <Schedule fontSize="large" />,
    title: 'Schedule Maker',
    description: 'Create optimized study schedules for better time management.',
    action: 'Make Schedule',
    link: '/schedule'
  }
];

function Dashboard({ serverStatus }) {
  // State for LICENSE AND MORE dropdown
  const [licenseMenuAnchor, setLicenseMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const [error, setError] = useState('');

  const handleLogout = async () => {
    try {
      await auth.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      setError('Failed to log out. Please try again.');
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Welcome to IX-Study
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* AI Features Grid */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, color: 'primary.main' }}>
                    {feature.icon}
                  </Box>
                  <Typography gutterBottom variant="h5" component="h2" align="center">
                    {feature.title}
                  </Typography>
                  <Typography align="center" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button
                    component={feature.disabled ? 'button' : Link}
                    to={feature.disabled ? undefined : feature.link}
                    variant="contained"
                    color="primary"
                    sx={{
                      background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                      boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
                      pointerEvents: feature.disabled ? 'none' : 'auto',
                      cursor: feature.disabled ? 'not-allowed' : 'pointer'
                    }}
                    disabled={feature.disabled}
                  >
                    {feature.action}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Chat With AI Box */}
        <Box
          component={Paper}
          elevation={3}
          sx={{
            mt: 4,
            mb: 4,
            p: 3,
            borderRadius: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: 4,
            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: 6
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 0 } }}>
            <Chat sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" color="text.primary">
                Chat With AI
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Need help? Get instant answers to your questions from your AI study companion.
              </Typography>
            </Box>
          </Box>
          <Button
            component={Link}
            to="/askai"
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
            }}
          >
            Start Chat Now
          </Button>
        </Box>

        {/* Footer with account info */}
        <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
            <Button
              component={Link}
              to="/terms"
              size="small"
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              Terms of Service
            </Button>
            <Button
              component={Link}
              to="/privacy"
              size="small"
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              Privacy Policy
            </Button>
            <Button
              component={Link}
              to="/founder"
              size="small"
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              Meet the Founder
            </Button>
            {/* LICENSE AND MORE dropdown */}
            <Box>
              <Button
                id="license-menu-btn"
                aria-controls={open ? 'license-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={e => setLicenseMenuAnchor(e.currentTarget)}
                color="inherit"
                size="small"
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                LICENSE AND MORE
              </Button>
              <Menu
                id="license-menu"
                anchorEl={licenseMenuAnchor}
                open={Boolean(licenseMenuAnchor)}
                onClose={() => setLicenseMenuAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              >
                <MenuItem component="a" href="/COPYRIGHT.txt" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>COPYRIGHT.txt</MenuItem>
                <MenuItem component="a" href="/LICENSE.txt" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>LICENSE.txt</MenuItem>
                <MenuItem component="a" href="/LICENSES.txt" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>LICENSES.txt</MenuItem>
                <MenuItem component="a" href="/README.txt" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>README.txt</MenuItem>
                <MenuItem component="a" href="/TRADEMARK.txt" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>TRADEMARK.txt</MenuItem>
                <MenuItem component="a" href="/sitemap.xml" target="_blank" rel="noopener noreferrer" onClick={() => setLicenseMenuAnchor(null)}>sitemap.xml</MenuItem>
              </Menu>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Dashboard;